/**
 * @file System.Themes.types.ts
 * @description Theme-related types, constants, and color definitions
 * 
 * Import this file directly into code, do not route via System.types.ts
 */


/**
 * @constant UIElementTypeDisplayNames
 * @description Display names for UI element types used in the bulk editor
 * @readonly
 */
export const UIElementTypeDisplayNames = {
    Fields: 'Fields',
    Records: 'Records',
    RecordGroups: 'Record Groups',
    MetaFields: 'Meta Fields',
    FormProperties: 'Form Properties',
    Attachments: 'Attachments',
    ReferencedList: 'Referenced By',
    Tables: 'Tables',
} as const;

/**
 * @constant UIElementTypeColors
 * @description Standardized colors for UI element types used across the application
 * @readonly
 * 
 * Colors are Ant Design color names that work with both Tag components and theme tokens
 */
export const UIElementTypeColors = {
    Fields: 'blue',
    Records: 'gold',
    'Record Groups': 'gold',
    MetaFields: 'purple',
    FormProperties: 'yellow',
    Attachments: 'cyan',
    'Referenced By': 'geekblue',
    Tables: 'magenta',
    Containers: 'green',
    'Meta Fields': 'purple',
    'Form Properties': 'yellow'
} as const;


/**
 * @constant ThemeColors
 * @description Theme colors
 * @readonly
 */
export const ThemeColors = {
    PRIMARY: {
        LIGHT: '#F25921',
        DARK: '#F25921',
    },
    PRIMARY_DIM: {
        LIGHT: '#F25921',
        DARK: '#C4471A',
    },
    PRIMARY_L: {
        LIGHT: '#FFE3D9',
        DARK: '#371308',
    },
    PRIMARY_XL: {
        LIGHT: '#FFF0E8',
        DARK: '#27140a',
    },
    BLACK_ALPHA: {
        P85: 'rgba(0, 0, 0, 0.85)',
        P68: 'rgba(0, 0, 0, 0.68)',
        P60: 'rgba(0, 0, 0, 0.6)',
    },
    WHITE_ALPHA: {
        P95: 'rgba(255, 255, 255, 0.95)',
        P85: 'rgba(255, 255, 255, 0.85)',
        P68: 'rgba(255, 255, 255, 0.68)',
    },
    TRANSPARENT_BG: {
        LIGHT: 'rgba(252,252,252,0.75)',
        DARK: 'rgba(50,50,50,0.75)',
    },
    FIELD_BG: {
        LIGHT: '#FFFFFF',
        DARK: '#212121',
    },
    FIELD_ALT: {
        LIGHT: '#F0F0F0',
        DARK: '#101010',
    },
    APP_BG: {
        LIGHT: 'rgb(245, 245, 245)',
        DARK: 'rgb(0, 0, 0)',
    },
    FIELD_SELECT: {
        LIGHT: '#EBEBEB',
        DARK: '#3A3A3A',
    },
    SOLID: {
        RED: '#FF1010',
    },
    /** `--syn-line`: panel borders and header rules. */
    LINE: {
        LIGHT: '#E0E0E0',
        DARK: '#333333',
    },
    /** `--syn-tide`: selection ring, focus, progress. */
    TIDE: {
        LIGHT: '#2EB7C6',
        DARK: '#2EB7C6',
    },
    /** `--syn-tide-wash`: selected fill and the Approve schedule button. */
    TIDE_WASH: {
        LIGHT: '#DEF3F5',
        DARK: '#0E3236',
    },
    /** `--syn-tide-deep`: primary text, links, Edited, and the Publish edition fill. */
    TIDE_DEEP: {
        LIGHT: '#0F7E8C',
        DARK: '#1E9BA9',
    },
    /** `--syn-success`: Verified, Passed, the high confidence band. */
    SUCCESS: {
        LIGHT: '#2E7D5B',
        DARK: '#49AA7F',
    },
    /** `--syn-warning`: Flagged, the mid confidence band. */
    WARNING: {
        LIGHT: '#B8731A',
        DARK: '#D18B2C',
    },
    /** `--syn-primary-50`: a row that needs attention. */
    PRIMARY_50: {
        LIGHT: '#FFF2F0',
        DARK: '#2A1215',
    },
    /** Draft edition tag (dashed) and the crop boundary. */
    DRAFT_BORDER: {
        LIGHT: '#BDBDBD',
        DARK: '#5A5A5A',
    },
} as const;

/**
 * @constant Motion
 * @description The spec's motion values: 120 ms transitions, a 240 ms viewer
 * scroll-to-box, and `--syn-ease` for both. The CSS curve and its control
 * points are the same curve; the points drive animations run in JS.
 * @readonly
 */
export const Motion = {
    TRANSITION_MS: 120,
    SCROLL_TO_BOX_MS: 240,
    EASE: 'cubic-bezier(0.2, 0, 0, 1)',
    EASE_POINTS: [0.2, 0, 0, 1],
} as const;

/** Monospace family for values, word IDs, hashes and confidence. */
export const FONT_FAMILY_CODE = "'Source Code Pro', monospace";

/**
 * Font size presets for user preference
 * Values represent base fontSize in pixels for Ant Design ConfigProvider
 */
export const FontSizePresets = {
    xs: 12,
    small: 14,
    medium: 16,
    large: 18,
    xl: 20,
} as const;

export type FontSizePreference = keyof typeof FontSizePresets;
export const FONT_SIZE_DEFAULT: FontSizePreference = 'medium';

/**
 * @constant AgentMarkdownFontScale
 * @description Font size multipliers for agent message markdown rendering, relative to base fontSize token.
 * @readonly
 */
export const AgentMarkdownFontScale = {
    small: 0.85,
    p: 0.93,
    h1: 1.5,
    h2: 1.4,
    h3: 1.3,
    h4: 1.2,
    h5: 1.1,
    h6: 1,
} as const;

/**
 * PDF Export Styling Parameters
 * All visual styling values for PDF generation
 */

// Typography
export const PDF_FONT_FAMILY = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
export const PDF_FONT_LINK = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap';
export const PDF_FONT_SIZE_BASE = '13px';
export const PDF_FONT_SIZE_BODY = '13pt';
export const PDF_FONT_SIZE_FIELD_VALUE = '13pt';
export const PDF_FONT_SIZE_HEADING_3 = '24px';
export const PDF_FONT_SIZE_HEADING_4 = '20px';
export const PDF_FONT_SIZE_HEADING_5 = '16px';
export const PDF_FONT_SIZE_HEADER = '10pt';
export const PDF_FONT_SIZE_FOOTER = '6pt';
export const PDF_LINE_HEIGHT = 1.5;
export const PDF_LINE_HEIGHT_HEADING_5 = 1;
export const PDF_FONT_WEIGHT_LABEL = 600;
export const PDF_FONT_WEIGHT_VALUE = 400;
export const PDF_FONT_WEIGHT_STRONG = 500;

// Spacing & Layout
export const PDF_BORDER_RADIUS = 4;
export const PDF_BORDER_RADIUS_LG = 4;
export const PDF_FORM_ITEM_MARGIN_BOTTOM = 10;
export const PDF_FORM_VERTICAL_LABEL_PADDING = 0;
export const PDF_CASCADER_DROPDOWN_HEIGHT = 400;

// Colors
export const PDF_HIDE = 'transparent';
export const PDF_COLOR_TEXT = '#000';
export const PDF_COLOR_ERROR = '#ff4d4f';
export const PDF_COLOR_TEXT_SECONDARY = '#666';
export const PDF_COLOR_BACKGROUND = '#FFFFFF';
export const PDF_COLOR_BG_SHADED = '#f0f0f0';

// Page Layout
export const PDF_PAGE_FORMAT = 'letter' as const;
export const PDF_PAGE_LANDSCAPE = true;
export const PDF_MARGIN_TOP = '15mm';
export const PDF_MARGIN_RIGHT = '10mm';
export const PDF_MARGIN_BOTTOM = '20mm';
export const PDF_MARGIN_LEFT = '10mm';

// Physical page dimensions (US Letter) and unit conversion for print-width math
export const PDF_PAGE_WIDTH_MM_PORTRAIT = 215.9;
export const PDF_PAGE_WIDTH_MM_LANDSCAPE = 279.4;
export const PDF_DEFAULT_MARGIN_MM = 15;
export const MM_PER_INCH = 25.4;
export const CSS_PX_PER_INCH = 96;

// Export Container (wider for landscape)
export const PDF_EXPORT_CONTAINER_WIDTH = 1400;
export const PDF_EXPORT_CONTAINER_PADDING = '1px';

// Control Sizing
export const PDF_EXPORT_MIN_CONTROL_HEIGHT = '1.8lh';

// Header & Footer
export const PDF_HEADER_PADDING = '0mm 10mm';
export const PDF_HEADER_LOGO_HEIGHT = '20px';
export const PDF_FOOTER_PADDING = '0mm 10mm';
export const PDF_FOOTER_BRAND_TEXT = 'Powered by Syncretic';
export const PDF_FOOTER_BRAND_URL = 'https://syncretic.work';

// Preview Modal
export const PDF_PREVIEW_WIDTH = 450;
export const PDF_PREVIEW_HEIGHT = 600;
export const PDF_MODAL_WIDTH = 1000;

// PDF Export Overrides
export const PDF_EXPORT_STYLE_OVERRIDES = `
    /* Global font normalization — ensures consistent font across all elements */
    body, div, span, input, select, textarea, p, a {
        font-family: 'Work Sans', sans-serif !important;
    }

    /* Normalize font size for table data cells (not headers) */
    [role="row"] div,
    [role="row"] span,
    [role="row"] input {
        font-size: ${PDF_FONT_SIZE_BASE} !important;
    }

    /* Hide certain elements */
    button,
    .ant-btn,
    .ant-btn-icon,
    .ant-select-arrow,
    .ant-select-suffix,
    .anticon-down,
    .anticon-close-circle,
    .anticon-function,
    .ant-empty,
    .ant-picker-suffix,
    .ant-picker-clear,
    .ant-upload-list-item-actions,
    .ant-upload-drag,
    .ant-upload-dragger,
    .ant-cascader-picker-arrow,
    .ant-input-clear-icon {
        display: none !important;
        width: 0 !important;
    }

    /* Normalize font weight — Select items should match Input text */
    .ant-select-selection-item {
        font-weight: ${PDF_FONT_WEIGHT_VALUE} !important;
    }

    /* Inside table rows, make all controls transparent so row background shows through */
    [role="row"] .ant-input,
    [role="row"] .ant-input-affix-wrapper,
    [role="row"] .ant-input-number,
    [role="row"] .ant-input-number-input,
    [role="row"] .ant-input-number-affix-wrapper,
    [role="row"] .ant-select-selector,
    [role="row"] .ant-select-single,
    [role="row"] .ant-picker {
        background: transparent !important;
        background-color: transparent !important;
    }

    /* Inside data rows, override inline cell styles to top-align all content.
       Header cells (role="columnheader") and aggregation cells (role="cell") are unaffected. */
    [role="row"] > div {
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        align-items: flex-start !important;
    }

    [role="row"] .ant-input,
    [role="row"] .ant-input-affix-wrapper,
    [role="row"] .ant-input-number,
    [role="row"] .ant-input-number-input,
    [role="row"] .ant-input-number-affix-wrapper,
    [role="row"] .ant-select-selector,
    [role="row"] .ant-select-selection-item,
    [role="row"] .ant-select-single,
    [role="row"] .ant-picker,
    [role="row"] .ant-picker-input input {
        min-height: 0 !important;
        height: auto !important;
        max-height: none !important;
        padding: 1px 2px !important;
        align-items: flex-start !important;
    }

    /* Force Select items to wrap instead of truncating with ellipsis */
    .ant-select,
    .ant-select-selection-item,
    .ant-select-selector,
    .ant-select-single {
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
        height: auto !important;
        max-height: none !important;
    }

    /* SYD-2058: wrap table headers and cells at word boundaries only. ArTable sets
       overflow-wrap:anywhere inline for PDF, which breaks words mid-character in the
       narrow print columns and also collapses each column's min-content to ~1ch.
       Forcing normal wrapping keeps whole words intact, so the minmax(min-content, …)
       tracks size to the widest word — never splitting one. */
    [role="columnheader"],
    [role="columnheader"] *,
    [role="row"] *,
    [role="cell"] * {
        overflow-wrap: normal !important;
        word-break: normal !important;
    }

    /* Normalize composite field preview text — same color/weight as regular values */
    [aria-label^="open-composite-field-"],
    [aria-label^="open-composite-field-"] span {
        color: ${PDF_COLOR_TEXT} !important;
        font-weight: ${PDF_FONT_WEIGHT_VALUE} !important;
    }

    /* Ensure table takes full width */
    .ant-table,
    .ant-table-wrapper,
    .ant-table-container,
    .ant-table-content {
        width: 100% !important;
        max-width: 100% !important;
    }

    /* Remove all borders from form controls */
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-input-number,
    .ant-input-number-affix-wrapper,
    .ant-input-textarea,
    .ant-select-selector,
    .ant-select-single,
    .ant-picker,
    .ant-cascader,
    .ant-input-group-addon,
    .ant-input-number-group-addon {
        border: none !important;
        box-shadow: none !important;
    }

    /* Ensure controls wrap and take full height */
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-input-number,
    .ant-input-number-input,
    .ant-input-number-affix-wrapper,
    .ant-input-textarea textarea,
    .ant-select-selector,
    .ant-select-selection-item,
    .ant-select-selection-search-input,
    .ant-select-single,
    .ant-picker,
    .ant-picker-input input,
    .ant-cascader,
    .ant-cascader-input {
        height: auto !important;
        min-height: ${PDF_EXPORT_MIN_CONTROL_HEIGHT} !important;
        line-height: 1.4 !important;
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
    }

    /* Prevent number/currency wrapping — force prefix ($) and input on the same line.
       min-height applies to form-header fields; inside [role="row"], overridden to 0. */
    .ant-input-number-input,
    .ant-input-number,
    .ant-input-number-affix-wrapper {
        white-space: nowrap !important;
        min-height: ${PDF_EXPORT_MIN_CONTROL_HEIGHT} !important;
    }

    .ant-input-number-affix-wrapper {
        display: inline-flex !important;
        flex-wrap: nowrap !important;
        align-items: flex-start !important;
        overflow-wrap: normal !important;
        word-break: normal !important;
    }

    .ant-input-number,
    .ant-input-number-input-wrap {
        display: flex !important;
        align-items: flex-start !important;
        min-height: 0 !important;
    }

    .ant-input-number-input {
        padding: 1px 2px !important;
    }

    .ant-input-number-prefix {
        flex-shrink: 0 !important;
        overflow-wrap: normal !important;
        word-break: normal !important;
    }

    .ant-input-number-handler-wrap {
        display: none !important;
    }

    /* Keep composite field previews on one line */
    [aria-label^="open-composite-field-"] {
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
    }

    /* Keep labels grouped with their controls. Both attributes are emitted by
       React (FieldUI, MetaFieldUI, FieldLabel), never inferred from the DOM, so
       they land on form fields only and never on an ArTable. */
    [data-pdf-field-group='true'] {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    [data-pdf-label='true'] {
        page-break-after: avoid;
        break-after: avoid-page;
    }
`;
